import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useInvoiceDetails } from 'pages/invoices/hooks/useInvoiceDetails';
import { InvoiceTabAlert } from 'pages/invoices/util/invoiceTabAlerts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { BulkDiscountCard, useBulkDiscountGate } from '../views/details-tab/BulkDiscountControl';
import { InvoiceCaseCard } from '../views/details-tab/invoice-matters/InvoiceCaseCard';
import { useInvoiceAlertNodes } from '../views/details-tab/InvoiceAlerts';
import { CaseTotals, CaseTotalsLockNotice } from '../views/totals-tab/CaseTotals';
import { byCaseNumber } from './invoiceSections';
import { InvoiceTabProps } from './invoiceTabProps';
import { InvoiceVariantNotice } from './InvoiceVariantNotice';

/**
 * The Cases tab of the new design. The alerts with an action lead it. Then one group per case, its card and its Case
 * totals together: the cases that have an action (a changed fee arrangement, an approval of this user's, fees over the
 * cap) first, then the others by case number. The unsigned-agreement alert follows the card of the first case it
 * names. A joint invoice's Bulk discount comes after the cases it applies to. A prepayment invoice, which bills no
 * work, has its Case totals alone.
 */
export function CasesTab({
    invoice,
    classifiers,
    isInViewMode,
    isLoading,
    alerts,
    addNewInvoiceItems,
    setSuccessMessages,
    addedInvoiceTimecardIds,
    addedInvoiceExpenseIds,
    handleStatusLoadingState,
}: InvoiceTabProps) {
    const { isPrepayment } = useInvoice(invoice);
    const { isDetailsReadOnly } = useInvoiceDetails(invoice);
    const bulkDiscount = useBulkDiscountGate(invoice);
    const { nodes } = useInvoiceAlertNodes({
        invoice,
        addNewInvoiceItems,
        setSuccessMessages,
        addedTimecardCount: addedInvoiceTimecardIds.length,
        addedExpenseCount: addedInvoiceExpenseIds.length,
    });
    const caseTotals = (matterId?: number) => (
        <CaseTotals
            invoice={invoice}
            classifiers={classifiers}
            isInViewMode={isInViewMode}
            isLoading={isLoading}
            matterId={matterId}
        />
    );
    if (isPrepayment) {
        return caseTotals();
    }

    const isReadOnly = isInViewMode || isDetailsReadOnly;
    const alertNodes = (list: InvoiceTabAlert[]) => list.flatMap((alert) => (nodes[alert.id] ? [nodes[alert.id]] : []));
    const leadingMatterIds = new Set(alerts.filter((alert) => alert.isAction).map((alert) => alert.matterId));
    const matters = (invoice.invoiceMatters ?? []).toSorted(byCaseNumber);
    // The cap error holds the next step, so the case over its cap leads with it.
    const hasCapError = alerts.some((alert) => alert.id === 'cap-error');
    const leads = (matterId: number, hasMatterCapError: boolean) =>
        leadingMatterIds.has(matterId) || (hasCapError && hasMatterCapError);
    const orderedMatters = [
        ...matters.filter((matter) => leads(matter.matterId, matter.hasMatterCapError)),
        ...matters.filter((matter) => !leads(matter.matterId, matter.hasMatterCapError)),
    ];
    const isJoint = matters.length > 1;
    // Each case's own alerts are drawn in its card; the editing mark has no card of its own.
    const invoiceAlerts = alerts.filter((alert) => alert.matterId === undefined && alert.id !== 'editing');
    const otherAlerts = alertNodes(invoiceAlerts.filter((alert) => !alert.isAction));
    const unsignedMatterId = orderedMatters.find((matter) => matter.hasUnsignedServiceAgreement)?.matterId;

    return (
        <>
            <InvoiceVariantNotice invoice={invoice} />
            {alertNodes(invoiceAlerts.filter((alert) => alert.isAction))}
            {unsignedMatterId === undefined ? otherAlerts : null}
            <CaseTotalsLockNotice invoice={invoice} />
            {orderedMatters.map((invoiceMatter, index) => (
                <div
                    key={invoiceMatter.matterId}
                    role="group"
                    aria-label={t('invoices.details.case', { fullName: invoiceMatter.fullName })}
                    className={cn('flex flex-col gap-(--card-gap)', index > 0 && 'mt-6')}
                >
                    <InvoiceCaseCard
                        invoice={invoice}
                        invoiceMatter={invoiceMatter}
                        classifiers={classifiers}
                        isInViewMode={isReadOnly}
                        handleStatusLoadingState={handleStatusLoadingState}
                    />
                    {invoiceMatter.matterId === unsignedMatterId ? otherAlerts : null}
                    {caseTotals(invoiceMatter.matterId)}
                </div>
            ))}
            {!isReadOnly && isJoint ? (
                <div className="mt-6">
                    <BulkDiscountCard key={bulkDiscount.key} invoice={invoice} disabled={bulkDiscount.isDisabled} />
                </div>
            ) : null}
        </>
    );
}
